import {
  Pagination,
  Table,
} from "components/table";

import Button from "components/button";

import Typography from "components/typography";

import DeleteDriverModal from "../detail/components/delete-driver-modal";
import DeactivateDriverModal from "../detail/components/deactivate-driver-modal";
import ConfirmationModal from "components/shared/confirmation-modal";
import ExpandableList from "components/shared/expandable-list";

import ImportSidePanel from "components/shared/import-side-panel";

import ExportSidePanel from "components/shared/export-side-panel";

import Input from "components/input";

import Select from "components/select";

import Icon from "components/icon";

import { useAppTranslation } from "locale/useAppTranslation";
import { NamespaceKeys } from "locale/types";
import { DriverRow } from "_interfaces/driver.interfaces";

import useDriverList from "./useDriverList";

export const driverListPageRouteName =
  "/master-data/driver";

type DriverTranslation = (
  key: NamespaceKeys<"driver">,
  options?: Record<string, unknown>,
) => string;

const getDeleteSuccessTitle = (
  lastDeletedDriver: DriverRow | null,
  t: DriverTranslation,
): string => {
  if (lastDeletedDriver) {
    return t("modal-success-delete-title") || "Deleted Successfully";
  }
  return t("bulk-delete-success-title") || "Successfully Deleted!";
};

const getDeleteSuccessMessage = (
  lastDeletedDriver: DriverRow | null,
  lastDeletedCount: number,
  t: DriverTranslation,
): string => {
  if (lastDeletedDriver) {
    return (
      t("modal-success-delete-message", { name: lastDeletedDriver.driverName }) ||
      `${lastDeletedDriver.driverName} has been successfully deleted.`
    );
  }

  if (lastDeletedCount === 1) {
    return (
      t("bulk-delete-success-message", { count: lastDeletedCount }) ||
      "1 Driver successfully deleted"
    );
  }

  return (
    t("bulk-delete-success-message_plural", { count: lastDeletedCount }) ||
    `${lastDeletedCount} Drivers successfully deleted`
  );
};

const DriverListPage = () => {
  const { t } = useAppTranslation("driver");

  const {
    columns,
    data,
    totalData,
    totalPages,
    isLoading,
    isFetching,

    selectedKeys,
    setSelectedKeys,

    sortState,
    setSortState,

    currentPage,
    setCurrentPage,

    rowsPerPage,
    setRowsPerPage,

    bulkAction,
    bulkActionOptions,
    setBulkAction,
    handleApplyBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    selectedDriverNames,
    onConfirmBulkStatusUpdate,

    isDeleteOpen,
    setIsDeleteOpen,
    isDeleteSuccessOpen,
    setIsDeleteSuccessOpen,
    handleConfirmDelete,
    isDeleting,
    isDeactivateOpen,
    setIsDeactivateOpen,
    handleConfirmDeactivate,
    deactivateTargetDriver,
    selectedDriver,
    setSelectedDriver,
    lastDeletedDriver,
    setLastDeletedDriver,
    lastDeletedCount,

    isImportOpen,
    setIsImportOpen,
    selectedImportFile,
    importValidationErrors,
    handleImportSubmit,
    handleImportFileSelect,
    handleImportFileRemove,
    handleDownloadTemplate,
    isImportLoading,

    onExport,
    isExportOpen,
    setIsExportOpen,

    fileFormat,
    setFileFormat,

    exportType,
    setExportType,

    handleExportSubmit,

    exportFileFormatOptions,
    exportTypeOptions,

    search,
    setSearch,

    statusFilter,
    setStatusFilter,

    totalSelected,

    onAddDriver,
    onRowClick,
    draftCount,
    onDraft,
  } = useDriverList();

  return (
    <div className="flex flex-col gap-6">
      <section className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {totalSelected > 0 ? (
            <>
              <select
                value={bulkAction}
                onChange={(e) =>
                  setBulkAction(
                    e.target.value,
                  )
                }
                disabled={bulkActionOptions.length === 0}
                className="h-11 rounded-xl border border-neutral-200 bg-white px-4 text-sm outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {t("bulk-actions")}
                </option>
                {bulkActionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <Button
                onClick={
                  handleApplyBulkAction
                }
                disabled={!bulkAction || bulkActionOptions.length === 0}
              >
                {t("btn-apply")}
              </Button>

              <Typography variant="bodyMedium">
                {t("selected-count", { count: totalSelected })}
              </Typography>
            </>
          ) : (
            <Typography variant="bodyMedium">
              {t("selected-count-zero")}
            </Typography>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="draft" size={15} />}
            className="relative"
            onClick={onDraft}
            data-testid="btn-draft"
          >
            {t("btn-draft")}
            {draftCount > 0 && (
              <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 shadow-sm ring-2 ring-white">
                {draftCount}
              </span>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="export" size={15} />}
            onClick={onExport}
            data-testid="btn-export-driver"
          >
            {t("btn-export")}
          </Button>

          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="import" size={15} />}
            onClick={() =>
              setIsImportOpen(
                true,
              )
            }
            data-testid="btn-import-driver"
          >
            {t("btn-import")}
          </Button>

          <Button
            type="button"
            variant="fill"
            color="primary"
            size="medium"
            iconLeft={<Icon name="add" size={16} />}
            onClick={onAddDriver}
            data-testid="btn-add-driver"
          >
            {t("btn-add-driver")}
          </Button>
        </div>
      </section>

      <section className={`rounded-2xl border border-border bg-white p-6 shadow-sm transition-opacity duration-200 ${isFetching && !isLoading ? "opacity-60 pointer-events-none" : ""}`}>
        {/* FILTER */}
        <div className="mb-6 flex items-center gap-4 relative z-30">
          <div className="flex-1">
            <Input
              placeholder={t("placeholder-search")}
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value,
                )
              }
              leftIcon={
                <Icon
                  name="search"
                  size={18}
                />
              }
            />
          </div>

          <div className="w-[220px]">
            <Select
              value={statusFilter}
              onValueChange={(
                value,
              ) =>
                setStatusFilter(
                  Array.isArray(
                    value,
                  )
                    ? value[0]
                    : value,
                )
              }
              options={[
                {
                  label: t("filter-status-placeholder") || "All Status",
                  value: "",
                },
                {
                  label: t("status-available") || "Available",
                  value: "available",
                },
                {
                  label: t("status-assigned") || "Assigned",
                  value: "assigned",
                },
                {
                  label: t("status-inactive") || "Inactive",
                  value: "inactive",
                },
              ]}
            />
          </div>

          {(search ||
            statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter(
                    "",
                  );
                }}
                className="text-sm text-neutral-500"
              >
                {t("btn-clear-all")}
              </button>
            )}
        </div>

        <Table
          data={data}
          loading={isLoading}
          columns={columns}
          rowKey="id"
          selectable
          selectedRowKeys={
            selectedKeys
          }
          onSelectedRowKeysChange={
            setSelectedKeys
          }
          sortState={
            sortState
          }
          onSortStateChange={
            setSortState
          }
          currentPage={currentPage}
          limit={rowsPerPage}
          onRowClick={
            onRowClick
          }
          stickySelectable
        />

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Typography variant="bodySmall">
              {t("rows-per-page")}
            </Typography>

            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(
                  Number(
                    e.target.value,
                  ),
                );

                setCurrentPage(1);
              }}
              className="h-9 rounded-lg border border-border px-3 text-sm"
            >
              <option value={10}>
                10
              </option>

              <option value={20}>
                20
              </option>

              <option value={50}>
                50
              </option>
            </select>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={
              totalPages
            }
            onPageChange={
              setCurrentPage
            }
          />
        </div>

        <div className="mt-3">
          <Typography variant="bodySmall">
            {t("showing-data", {
              showing: data.length,
              total: totalData,
            })}
          </Typography>
        </div>
      </section>

      <DeactivateDriverModal
        open={isDeactivateOpen}
        isBulk={!deactivateTargetDriver && selectedKeys.length > 0}
        onClose={() => setIsDeactivateOpen(false)}
        onConfirm={handleConfirmDeactivate}
        driverName={deactivateTargetDriver?.driverName}
        selectedDriverNames={deactivateTargetDriver ? [] : selectedDriverNames}
      />

      <DeleteDriverModal
        open={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedDriver(null);
        }}
        onConfirm={handleConfirmDelete}
        driverName={selectedDriver?.driverName}
        selectedDriverNames={selectedDriver ? [] : selectedDriverNames}
        isLoading={isDeleting}
      />

      <ConfirmationModal
        open={isDeleteSuccessOpen}
        onCancel={() => {
          setIsDeleteSuccessOpen(false);
          setLastDeletedDriver(null);
        }}
        onConfirm={() => {
          setIsDeleteSuccessOpen(false);
          setLastDeletedDriver(null);
        }}
        title={getDeleteSuccessTitle(lastDeletedDriver, t)}
        message={getDeleteSuccessMessage(lastDeletedDriver, lastDeletedCount, t)}
        confirmLabel={t("btn-close") || "Close"}
        hideCancelButton
        icon="success"
      />

      <ConfirmationModal
        open={showBulkConfirmModal}
        title={(() => {
          const status = bulkAction.toLowerCase();
          if (status === "activate") return t("bulk-activate-title") || "Activate Drivers";
          if (status === "deactivate") return t("bulk-deactivate-title") || "Deactivate Drivers";
          if (status === "delete") return t("modal-delete-title") || "Delete Drivers";
          return "";
        })()}
        message={(() => {
          const status = bulkAction.toLowerCase();
          const count = totalSelected;
          if (status === "activate") return t("bulk-activate-message", { count }) || `Are you sure you want to activate ${count} Drivers?`;
          if (status === "deactivate") return t("bulk-deactivate-message", { count }) || `Are you sure you want to deactivate ${count} Drivers?`;
          if (status === "delete") return t("bulk-delete-message", { count }) || `Are you sure you want to delete ${count} Drivers?`;
          return "";
        })()}
        confirmLabel={(() => {
          const status = bulkAction.toLowerCase();
          if (status === "activate") return t("bulk-activate-confirm") || "Activate";
          if (status === "deactivate") return t("bulk-deactivate-confirm") || "Deactivate";
          if (status === "delete") return t("modal-ok-delete") || "Delete";
          return t("btn-confirm");
        })()}
        cancelLabel={t("btn-cancel")}
        onConfirm={onConfirmBulkStatusUpdate}
        onCancel={() => setShowBulkConfirmModal(false)}
        icon={bulkAction.toLowerCase() === "delete" ? "danger" : "warning"}
        loading={isDeleting}
        processingLabel={t("btn-processing")}
      >
        <ExpandableList items={selectedDriverNames} />
      </ConfirmationModal>

      <ImportSidePanel
        open={isImportOpen}
        onClose={() =>
          setIsImportOpen(
            false,
          )
        }
        onSubmit={
          handleImportSubmit
        }
        onTemplateDownload={
          handleDownloadTemplate
        }
        onFileSelect={
          handleImportFileSelect
        }
        onFileRemove={
          handleImportFileRemove
        }
        selectedFile={
          selectedImportFile
        }
        submitLoading={
          isImportLoading
        }
        acceptedFileTypes=".xlsx,.xls,.csv"
        templateFileName="Driver Import Template.xlsx"
      />

      <ExportSidePanel
        open={isExportOpen}
        onClose={() =>
          setIsExportOpen(
            false,
          )
        }
        onSubmit={
          handleExportSubmit
        }
        fileFormatOptions={
          exportFileFormatOptions
        }
        exportTypeOptions={
          exportTypeOptions
        }
        fileFormatValue={
          fileFormat
        }
        exportTypeValue={
          exportType
        }
        onFileFormatChange={
          setFileFormat
        }
        onExportTypeChange={
          setExportType
        }
      />
    </div>
  );
};

export default DriverListPage;